import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import ReportToolbar from "../components/reports/shared/ReportToolbar";
import { getReportBuilderComponent } from "../components/reports/reportBuilderRegistry";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./SalesPages.css";
import { authFetch, authJsonFetch } from "../utils/authFetch";

function ReportBuilderPage() {
  const { templateKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const savedReportId = new URLSearchParams(location.search).get("savedReportId");

  const [template, setTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateNotFound, setTemplateNotFound] = useState(false);

  const [coverageTypes, setCoverageTypes] = useState([]);
  const [coverageTypesLoading, setCoverageTypesLoading] = useState(false);
  const [coverageTypesError, setCoverageTypesError] = useState("");

  const [carriers, setCarriers] = useState([]);
  const [carriersLoading, setCarriersLoading] = useState(false);

  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  const [opportunityStatuses, setOpportunityStatuses] = useState([]);
  const [opportunityStatusesLoading, setOpportunityStatusesLoading] = useState(false);

  const [selectedOwnedCoverageTypes, setSelectedOwnedCoverageTypes] = useState([]);
  const [selectedMissingCoverageTypes, setSelectedMissingCoverageTypes] = useState([]);
  const [selectedCarrierId, setSelectedCarrierId] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [zipCodeFilter, setZipCodeFilter] = useState("");

  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    return { start, end };
  };

  const [soldPoliciesFilters, setSoldPoliciesFilters] = useState(() => {
    const range = getCurrentMonthRange();
    return [
      {
        field: "effective_date",
        operator: "between",
        value: range.start,
        value2: range.end
      }
    ];
  });

  const [policyBookFilters, setPolicyBookFilters] = useState([]);

  const [upcomingEffectiveFilters, setUpcomingEffectiveFilters] = useState(() => {
    const range = getCurrentMonthRange();
    return [
      {
        field: "effective_date",
        operator: "between",
        value: range.start,
        value2: range.end
      }
    ];
  });

  const [upcomingEffectivePreset, setUpcomingEffectivePreset] = useState("this_month");

  const [opportunityPipelineFilters, setOpportunityPipelineFilters] = useState([]);

  const [running, setRunning] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [savedReportName, setSavedReportName] = useState("");
  const [reportResults, setReportResults] = useState(null);
  const [hasRunOnce, setHasRunOnce] = useState(false);

  const [savedConfigReady, setSavedConfigReady] = useState(false);
  const skipNextAutoRunRef = useRef(false);

  const permissions = template?.permissions || {};
  const isCrossSell = template?.template_key === "cross_sell_report";
  const isSoldPolicies = template?.template_key === "sold_policies_report";
  const isPolicyBook = template?.template_key === "policy_book_of_business_report";
  const isUpcomingEffective = template?.template_key === "upcoming_effective_date_report";
  const isOpportunityPipeline = template?.template_key === "opportunity_pipeline_report";
  const BuilderComponent = getReportBuilderComponent(template?.template_key);

  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setTemplateLoading(true);
        setTemplateNotFound(false);
        setReportResults(null);

        const res = await authFetch(`/api/reports/templates/${templateKey}`);

        if (res.status === 404) {
          setTemplate(null);
          setTemplateNotFound(true);
          return;
        }

        if (!res.ok) {
          throw new Error("Failed to load report template");
        }

        const data = await res.json();
        setTemplate(data || null);
        setTemplateNotFound(false);
      } catch (err) {
        console.error("Load report template error:", err);
        setTemplate(null);
        setTemplateNotFound(true);
      } finally {
        setTemplateLoading(false);
      }
    };

    loadTemplate();
  }, [templateKey]);

  useEffect(() => {
    const normalizeArrayResponse = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.coverageTypes)) return payload.coverageTypes;
      if (Array.isArray(payload?.rows)) return payload.rows;
      if (Array.isArray(payload?.users)) return payload.users;
      return [];
    };

    const needsSharedLookups =
      isCrossSell ||
      isSoldPolicies ||
      isPolicyBook ||
      isUpcomingEffective ||
      isOpportunityPipeline ||
      templateKey === "cross_sell_report" ||
      templateKey === "sold_policies_report" ||
      templateKey === "policy_book_of_business_report" ||
      templateKey === "upcoming_effective_date_report" ||
      templateKey === "opportunity_pipeline_report";

    const loadCoverageTypes = async () => {
      if (!needsSharedLookups) return;

      try {
        setCoverageTypesLoading(true);
        setCoverageTypesError("");

        const candidateEndpoints = [
          `/api/settings/coverage-types`,
          `/api/coverage-types`
        ];

        let loadedCoverageTypes = [];
        let lastError = "";

        for (const endpoint of candidateEndpoints) {
          try {
            const res = await authFetch(endpoint);

            if (!res.ok) {
              lastError = `${endpoint} returned ${res.status}`;
              continue;
            }

            const data = await res.json();
            const normalized = normalizeArrayResponse(data);

            if (Array.isArray(normalized) && normalized.length > 0) {
              loadedCoverageTypes = normalized;
              break;
            }

            if (Array.isArray(normalized) && normalized.length === 0) {
              lastError = `${endpoint} returned an empty array`;
            }
          } catch (err) {
            lastError = `${endpoint} failed`;
          }
        }

        if (!loadedCoverageTypes.length) {
          setCoverageTypes([]);
          setCoverageTypesError(
            lastError || "Coverage types could not be loaded from the available endpoints."
          );
          return;
        }

        setCoverageTypes(loadedCoverageTypes);
      } catch (err) {
        console.error("Load coverage types error:", err);
        setCoverageTypes([]);
        setCoverageTypesError("Coverage types failed to load.");
      } finally {
        setCoverageTypesLoading(false);
      }
    };

    const loadCarriers = async () => {
      if (!needsSharedLookups) return;

      try {
        setCarriersLoading(true);

        const res = await authFetch(`/api/carriers`);

        if (!res.ok) {
          throw new Error("Failed to load carriers");
        }

        const data = await res.json();
        const normalized = normalizeArrayResponse(data);
        setCarriers(Array.isArray(normalized) ? normalized : []);
      } catch (err) {
        console.error("Load carriers error:", err);
        setCarriers([]);
      } finally {
        setCarriersLoading(false);
      }
    };

    const loadAgents = async () => {
      if (
        !(
          isSoldPolicies ||
          isPolicyBook ||
          isUpcomingEffective ||
          templateKey === "sold_policies_report" ||
          templateKey === "policy_book_of_business_report" ||
          templateKey === "upcoming_effective_date_report"
        )
      ) {
        return;
      }

      try {
        setAgentsLoading(true);

        const candidateEndpoints = [
          `/api/users`,
          `/api/account-users`,
          `/api/settings/users`
        ];

        let loadedAgents = [];

        for (const endpoint of candidateEndpoints) {
          try {
            const res = await authFetch(endpoint);

            if (!res.ok) continue;

            const data = await res.json();
            const normalized = normalizeArrayResponse(data);

            if (Array.isArray(normalized)) {
              loadedAgents = normalized
                .map((item) => ({
                  id: item.id || item.user_id,
                  name: item.name || item.full_name || item.email || `User ${item.id || item.user_id}`
                }))
                .filter((item) => item.id);
              break;
            }
          } catch (_) {}
        }

        setAgents(loadedAgents);
      } catch (err) {
        console.error("Load agents error:", err);
        setAgents([]);
      } finally {
        setAgentsLoading(false);
      }
    };

    const loadOpportunityStatuses = async () => {
      if (!(isOpportunityPipeline || templateKey === "opportunity_pipeline_report")) {
        return;
      }

      try {
        setOpportunityStatusesLoading(true);

        const res = await authFetch(`/api/opportunities/statuses/all`);

        if (!res.ok) {
          throw new Error("Failed to load opportunity statuses");
        }

        const data = await res.json();
        const closed = new Set([
          "Closed-Lost",
          "Closed-Not Interested",
          "Closed-Already Purchased"
        ]);
        const normalized = Array.isArray(data) ? data.filter((status) => !closed.has(status)) : [];
        setOpportunityStatuses(normalized);
      } catch (err) {
        console.error("Load opportunity statuses error:", err);
        setOpportunityStatuses([]);
      } finally {
        setOpportunityStatusesLoading(false);
      }
    };

    loadCoverageTypes();
    loadCarriers();
    loadAgents();
    loadOpportunityStatuses();
  }, [templateKey, isCrossSell, isSoldPolicies, isPolicyBook, isUpcomingEffective, isOpportunityPipeline]);

  useEffect(() => {
    if (
      !savedReportId ||
      (!isCrossSell && !isSoldPolicies && !isPolicyBook && !isUpcomingEffective && !isOpportunityPipeline)
    ) {
      return;
    }

    const loadSavedReport = async () => {
      try {
        const data = await authJsonFetch(`/api/reports/saved/${savedReportId}`);

        const config = data?.config || {};

        setSavedReportName(data?.name || "");
        skipNextAutoRunRef.current = true;

        if (config.templateKey === "cross_sell_report") {
          const includeConditions =
            Array.isArray(config.includeGroups) && config.includeGroups.length > 0
              ? config.includeGroups.flatMap((group) => group.conditions || [])
              : [];

          const excludeConditions =
            Array.isArray(config.excludeGroups) && config.excludeGroups.length > 0
              ? config.excludeGroups.flatMap((group) => group.conditions || [])
              : [];

          const ownedCoverage = includeConditions
            .filter((condition) => condition.field === "coverage_type_id")
            .flatMap((condition) => {
              if (condition.operator === "in") {
                return Array.isArray(condition.value) ? condition.value : [];
              }
              return condition.value ? [condition.value] : [];
            })
            .map(String);

          const missingCoverage = excludeConditions
            .filter((condition) => condition.field === "coverage_type_id")
            .map((condition) => String(condition.value))
            .filter(Boolean);

          const carrierCondition =
            includeConditions.find((condition) => condition.field === "carrier_id") ||
            excludeConditions.find((condition) => condition.field === "carrier_id");

          const stateCondition = (config.clientFilters || []).find(
            (filter) => filter.field === "state"
          );

          const zipCondition = (config.clientFilters || []).find(
            (filter) => filter.field === "zip_code"
          );

          setSelectedOwnedCoverageTypes(ownedCoverage);
          setSelectedMissingCoverageTypes(missingCoverage);
          setSelectedCarrierId(carrierCondition?.value ? String(carrierCondition.value) : "");
          setStateFilter(stateCondition?.value || "");
          setZipCodeFilter(zipCondition?.value || "");
        }

        if (config.templateKey === "sold_policies_report") {
          setSoldPoliciesFilters(Array.isArray(config.filters) ? config.filters : []);
        }

        if (config.templateKey === "policy_book_of_business_report") {
          setPolicyBookFilters(Array.isArray(config.filters) ? config.filters : []);
        }

        if (config.templateKey === "upcoming_effective_date_report") {
          setUpcomingEffectiveFilters(Array.isArray(config.filters) ? config.filters : []);
          setUpcomingEffectivePreset(config.datePreset || "this_month");
        }

        if (config.templateKey === "opportunity_pipeline_report") {
          const cleanedFilters = Array.isArray(config.filters)
            ? config.filters.filter((filter) =>
                ["status", "coverage_type_id", "carrier_id"].includes(filter.field)
              )
            : [];

          setOpportunityPipelineFilters(cleanedFilters);
        }

        setHasRunOnce(true);
        setSavedConfigReady(true);
      } catch (err) {
        console.error("Load saved report error:", err);
        alert(err.message || "Failed to load saved report");
      }
    };

    loadSavedReport();
  }, [savedReportId, isCrossSell, isSoldPolicies, isPolicyBook, isUpcomingEffective, isOpportunityPipeline]);

  const formatFieldName = (value) => {
    if (value === "related_record_name") return "Related Record Name";
    if (value === "status_name") return "Status";
    if (value === "opportunity_type") return "Opportunity Type";
    if (value === "next_step") return "Next Steps";
    if (value === "premium") return "Premium";
    if (value === "aor_user_id") return "AOR Agent";
    if (value === "agent_name") return "AOR Agent";
    if (value === "zip_code") return "ZIP Code";

    return String(value || "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getCoverageTypeName = (id) => {
    const match = coverageTypes.find((item) => String(item.id) === String(id));
    return match?.name || String(id);
  };

  const getCarrierName = (id) => {
    const match = carriers.find((item) => String(item.id) === String(id));
    return match?.name || match?.carrier_name || String(id);
  };

  const requiredExistingCoverageLabels = useMemo(
    () => selectedOwnedCoverageTypes.map(getCoverageTypeName),
    [selectedOwnedCoverageTypes, coverageTypes]
  );

  const targetCoverageGapLabels = useMemo(
    () => selectedMissingCoverageTypes.map(getCoverageTypeName),
    [selectedMissingCoverageTypes, coverageTypes]
  );

  const builtConfig = useMemo(() => {
    if (isCrossSell) {
      const includeConditions = [];

      if (selectedOwnedCoverageTypes.length > 0) {
        includeConditions.push({
          field: "coverage_type_id",
          operator: selectedOwnedCoverageTypes.length === 1 ? "equals" : "in",
          value:
            selectedOwnedCoverageTypes.length === 1
              ? selectedOwnedCoverageTypes[0]
              : selectedOwnedCoverageTypes,
          value2: ""
        });
      }

      if (selectedCarrierId) {
        includeConditions.push({
          field: "carrier_id",
          operator: "equals",
          value: selectedCarrierId,
          value2: ""
        });
      }

      const includeGroups =
        includeConditions.length > 0
          ? [
              {
                logic: "AND",
                conditions: includeConditions
              }
            ]
          : [];

      const excludeGroups = selectedMissingCoverageTypes.map((coverageTypeId) => {
        const conditions = [
          {
            field: "coverage_type_id",
            operator: "equals",
            value: coverageTypeId,
            value2: ""
          }
        ];

        if (selectedCarrierId) {
          conditions.push({
            field: "carrier_id",
            operator: "equals",
            value: selectedCarrierId,
            value2: ""
          });
        }

        return {
          logic: "AND",
          conditions
        };
      });

      const clientFilters = [];

      if (stateFilter) {
        clientFilters.push({
          field: "state",
          operator: "equals",
          value: stateFilter,
          value2: ""
        });
      }

      if (zipCodeFilter.trim()) {
        clientFilters.push({
          field: "zip_code",
          operator: "equals",
          value: zipCodeFilter.trim(),
          value2: ""
        });
      }

      return {
        templateKey: "cross_sell_report",
        selectedColumns: [
          "client_full_name",
          "active_policy_summary",
          "email",
          "phone",
          "street_address",
          "city",
          "state",
          "zip_code"
        ],
        includeGroups,
        excludeGroups,
        clientFilters,
        sorts: [
          {
            field: "client_full_name",
            direction: "asc"
          }
        ]
      };
    }

    if (isSoldPolicies) {
      return {
        templateKey: "sold_policies_report",
        selectedColumns: [
          "client_full_name",
          "coverage_type_name",
          "carrier_name",
          "product_name",
          "policy_number",
          "application_date",
          "effective_date",
          "status_name",
          "agent_name"
        ],
        filters: soldPoliciesFilters,
        sorts: [
          {
            field: "effective_date",
            direction: "asc"
          },
          {
            field: "client_full_name",
            direction: "asc"
          }
        ]
      };
    }

    if (isPolicyBook) {
      return {
        templateKey: "policy_book_of_business_report",
        selectedColumns: [
          "client_full_name",
          "coverage_type_name",
          "carrier_name",
          "product_name",
          "policy_number",
          "status_name",
          "effective_date",
          "premium",
          "state",
          "zip_code",
          "county",
          "agent_name"
        ],
        filters: policyBookFilters,
        sorts: [
          {
            field: "client_full_name",
            direction: "asc"
          },
          {
            field: "effective_date",
            direction: "asc"
          }
        ]
      };
    }

    if (isUpcomingEffective) {
      return {
        templateKey: "upcoming_effective_date_report",
        selectedColumns: [
          "client_full_name",
          "coverage_type_name",
          "carrier_name",
          "product_name",
          "policy_number",
          "application_date",
          "effective_date",
          "status_name",
          "agent_name"
        ],
        filters: upcomingEffectiveFilters,
        datePreset: upcomingEffectivePreset,
        sorts: [
          {
            field: "effective_date",
            direction: "asc"
          },
          {
            field: "client_full_name",
            direction: "asc"
          }
        ]
      };
    }

    if (isOpportunityPipeline) {
      return {
        templateKey: "opportunity_pipeline_report",
        selectedColumns: [
          "related_record_name",
          "status_name",
          "next_step",
          "coverage_type_name",
          "carrier_name",
          "product_name",
          "premium"
        ],
        filters: opportunityPipelineFilters,
        sorts: [
          {
            field: "related_record_name",
            direction: "asc"
          }
        ]
      };
    }

    return {
      templateKey: template?.template_key
    };
  }, [
    isCrossSell,
    isSoldPolicies,
    isPolicyBook,
    isUpcomingEffective,
    isOpportunityPipeline,
    template,
    selectedOwnedCoverageTypes,
    selectedMissingCoverageTypes,
    selectedCarrierId,
    stateFilter,
    zipCodeFilter,
    soldPoliciesFilters,
    policyBookFilters,
    upcomingEffectiveFilters,
    upcomingEffectivePreset,
    opportunityPipelineFilters
  ]);

  const validateBeforeRun = () => {
    if (!BuilderComponent) {
      alert("This report builder is not implemented yet.");
      return false;
    }

    if (
      isCrossSell &&
      selectedOwnedCoverageTypes.length === 0 &&
      selectedMissingCoverageTypes.length === 0 &&
      !selectedCarrierId &&
      !stateFilter &&
      !zipCodeFilter.trim()
    ) {
      alert("Choose at least one coverage type or filter before running the report.");
      return false;
    }

    return true;
  };

  const runReportRequest = async () => {
    return authJsonFetch(`/api/reports/run`, {
      method: "POST",
      body: JSON.stringify(builtConfig)
    });
  };

  const runAndSetResults = async () => {
    const data = await runReportRequest();
    setReportResults(data);
    return data;
  };

  useEffect(() => {
    if (!savedConfigReady) return;

    const runSavedConfig = async () => {
      try {
        setRunning(true);
        await runAndSetResults();
      } catch (err) {
        console.error("Run saved report error:", err);
        alert(err.message || "Failed to run saved report");
      } finally {
        setRunning(false);
        setSavedConfigReady(false);
      }
    };

    runSavedConfig();
  }, [savedConfigReady]);

  const handleRunReport = async () => {
    if (!validateBeforeRun()) return;

    try {
      setRunning(true);
      setHasRunOnce(true);
      return await runAndSetResults();
    } catch (err) {
      console.error("Run report error:", err);
      alert(err.message || "Failed to run report");
      return null;
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    if (!hasRunOnce) return;
    if (skipNextAutoRunRef.current) {
      skipNextAutoRunRef.current = false;
      return;
    }

    const autoRun = async () => {
      if (!validateBeforeRun()) return;

      try {
        setRunning(true);
        await runAndSetResults();
      } catch (err) {
        console.error("Auto-run report error:", err);
        alert(err.message || "Failed to refresh report");
      } finally {
        setRunning(false);
      }
    };

    if (isCrossSell) {
      autoRun();
    }
  }, [selectedCarrierId, stateFilter, zipCodeFilter]);

  useEffect(() => {
    if (!hasRunOnce || !isSoldPolicies) return;
    if (skipNextAutoRunRef.current) {
      skipNextAutoRunRef.current = false;
      return;
    }

    const autoRun = async () => {
      if (!validateBeforeRun()) return;

      try {
        setRunning(true);
        await runAndSetResults();
      } catch (err) {
        console.error("Auto-run sold policies error:", err);
        alert(err.message || "Failed to refresh report");
      } finally {
        setRunning(false);
      }
    };

    autoRun();
  }, [soldPoliciesFilters]);

  useEffect(() => {
    if (!hasRunOnce || !isPolicyBook) return;
    if (skipNextAutoRunRef.current) {
      skipNextAutoRunRef.current = false;
      return;
    }

    const autoRun = async () => {
      if (!validateBeforeRun()) return;

      try {
        setRunning(true);
        await runAndSetResults();
      } catch (err) {
        console.error("Auto-run policy book error:", err);
        alert(err.message || "Failed to refresh report");
      } finally {
        setRunning(false);
      }
    };

    autoRun();
  }, [policyBookFilters]);

  useEffect(() => {
    if (!hasRunOnce || !isUpcomingEffective) return;
    if (skipNextAutoRunRef.current) {
      skipNextAutoRunRef.current = false;
      return;
    }

    const autoRun = async () => {
      if (!validateBeforeRun()) return;

      try {
        setRunning(true);
        await runAndSetResults();
      } catch (err) {
        console.error("Auto-run upcoming effective error:", err);
        alert(err.message || "Failed to refresh report");
      } finally {
        setRunning(false);
      }
    };

    autoRun();
  }, [upcomingEffectiveFilters, upcomingEffectivePreset]);

  useEffect(() => {
    if (!hasRunOnce || !isOpportunityPipeline) return;
    if (skipNextAutoRunRef.current) {
      skipNextAutoRunRef.current = false;
      return;
    }

    const autoRun = async () => {
      if (!validateBeforeRun()) return;

      try {
        setRunning(true);
        await runAndSetResults();
      } catch (err) {
        console.error("Auto-run opportunity pipeline error:", err);
        alert(err.message || "Failed to refresh report");
      } finally {
        setRunning(false);
      }
    };

    autoRun();
  }, [opportunityPipelineFilters]);

  const handleClearFilters = () => {
    setSelectedCarrierId("");
    setStateFilter("");
    setZipCodeFilter("");
  };

  const handleSaveReport = async () => {
    if (!validateBeforeRun()) return;

    const defaultName =
      savedReportName ||
      `${template?.name || "Report"} ${new Date().toLocaleDateString()}`;

    const name = window.prompt("Enter a name for this saved report:", defaultName);

    if (!name || !name.trim()) return;

    try {
      setSavingReport(true);

      const data = await authJsonFetch(`/api/reports/saved`, {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          templateKey: builtConfig.templateKey,
          config: builtConfig
        })
      });

      setSavedReportName(data.name || name.trim());
      alert("Report saved successfully.");
    } catch (err) {
      console.error("Save report error:", err);
      alert(err.message || "Failed to save report");
    } finally {
      setSavingReport(false);
    }
  };

  const handleExportCsv = async () => {
    if (!validateBeforeRun()) return;

    try {
      setExportingCsv(true);

      const res = await authFetch(`/api/reports/export/csv`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(builtConfig)
      });

      if (!res.ok) {
        let message = "Failed to export CSV";
        try {
          const errorData = await res.json();
          message = errorData.message || message;
        } catch (_) {}
        throw new Error(message);
      }

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = `${builtConfig.templateKey || "report"}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Export CSV error:", err);
      alert(err.message || "Failed to export CSV");
    } finally {
      setExportingCsv(false);
    }
  };

  const handleExportPdf = async () => {
    if (!validateBeforeRun()) return;

    try {
      setExportingPdf(true);

      const results = reportResults || (await runReportRequest());

      if (!reportResults) {
        setReportResults(results);
        setHasRunOnce(true);
      }

      const doc = new jsPDF("landscape");
      const now = new Date();

      doc.setFontSize(18);
      doc.text(template?.name || "Report", 14, 16);

      doc.setFontSize(10);
      doc.text(`Generated: ${now.toLocaleString()}`, 14, 24);

      if (isCrossSell) {
        doc.text(
          `Required Existing Coverage: ${
            requiredExistingCoverageLabels.length
              ? requiredExistingCoverageLabels.join(", ")
              : "None selected"
          }`,
          14,
          32
        );

        doc.text(
          `Target Coverage Gap: ${
            targetCoverageGapLabels.length
              ? targetCoverageGapLabels.join(", ")
              : "None selected"
          }`,
          14,
          40
        );

        doc.text(
          `Carrier Filter: ${
            selectedCarrierId ? getCarrierName(selectedCarrierId) : "None selected"
          }`,
          14,
          48
        );

        doc.text(`State Filter: ${stateFilter || "None selected"}`, 14, 56);
        doc.text(`Zip Code Filter: ${zipCodeFilter.trim() || "None selected"}`, 14, 64);
      }

      if (isSoldPolicies || isUpcomingEffective || isPolicyBook) {
        const activeFilters = isSoldPolicies
          ? soldPoliciesFilters
          : isUpcomingEffective
            ? upcomingEffectiveFilters
            : policyBookFilters;

        const dateFilter = activeFilters.find((f) => f.field === "effective_date");
        const carrierFilter = activeFilters.find((f) => f.field === "carrier_id");
        const coverageFilter = activeFilters.find((f) => f.field === "coverage_type_id");
        const agentFilter = activeFilters.find((f) => f.field === "aor_user_id");
        const statusFilter = activeFilters.find((f) => f.field === "status");
        const stateValue = activeFilters.find((f) => f.field === "state")?.value || "All";
        const zipValue = activeFilters.find((f) => f.field === "zip_code")?.value || "All";
        const countyValue = activeFilters.find((f) => f.field === "county")?.value || "All";

        const coverageName =
          coverageTypes.find((item) => String(item.id) === String(coverageFilter?.value))?.name || "All";
        const carrierName =
          carriers.find((item) => String(item.id) === String(carrierFilter?.value))?.name || "All";
        const agentName =
          agents.find((item) => String(item.id) === String(agentFilter?.value))?.name || "All";

        doc.text(
          `Effective Date Range: ${dateFilter?.value || "All"} to ${dateFilter?.value2 || "All"}`,
          14,
          32
        );

        if (isUpcomingEffective) {
          doc.text(
            `Date Preset: ${
              upcomingEffectivePreset === "this_month"
                ? "This Month"
                : upcomingEffectivePreset === "next_month"
                  ? "Next Month"
                  : "Custom"
            }`,
            14,
            40
          );
          doc.text(`Coverage Type: ${coverageName}`, 14, 48);
          doc.text(`Carrier: ${carrierName}`, 14, 56);
          doc.text(`Agent: ${agentName}`, 14, 64);
        } else if (isPolicyBook) {
          doc.text(`Coverage Type: ${coverageName}`, 14, 40);
          doc.text(`Carrier: ${carrierName}`, 14, 48);
          doc.text(`Status: ${statusFilter?.value || "All"}`, 14, 56);
          doc.text(`Agent: ${agentName}`, 14, 64);
          doc.text(`State: ${stateValue} | ZIP: ${zipValue} | County: ${countyValue}`, 14, 72);
        } else {
          doc.text(`Coverage Type: ${coverageName}`, 14, 40);
          doc.text(`Carrier: ${carrierName}`, 14, 48);
          doc.text(`Agent: ${agentName}`, 14, 56);
        }
      }

      if (isOpportunityPipeline) {
        const statusFilter = opportunityPipelineFilters.find((f) => f.field === "status");
        const carrierFilter = opportunityPipelineFilters.find((f) => f.field === "carrier_id");
        const coverageFilter = opportunityPipelineFilters.find((f) => f.field === "coverage_type_id");

        const coverageName =
          coverageTypes.find((item) => String(item.id) === String(coverageFilter?.value))?.name || "All";
        const carrierName =
          carriers.find((item) => String(item.id) === String(carrierFilter?.value))?.name || "All";

        doc.text(`Status: ${statusFilter?.value || "All Open Statuses"}`, 14, 32);
        doc.text(`Coverage Type: ${coverageName}`, 14, 40);
        doc.text(`Carrier: ${carrierName}`, 14, 48);
      }

      autoTable(doc, {
        startY: isPolicyBook ? 80 : 72,
        head: [results.selectedColumns.map((column) => formatFieldName(column))],
        body: results.rows.map((row) =>
          results.selectedColumns.map((column) => row[column] ?? "-")
        ),
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [15, 31, 51]
        },
        theme: "grid"
      });

      doc.save(`${builtConfig.templateKey || "report"}.pdf`);
    } catch (err) {
      console.error("Export PDF error:", err);
      alert(err.message || "Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  if (templateLoading) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Loading report builder...</div>
        </div>
      </>
    );
  }

  if (templateNotFound || !template) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Report template not found.</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">{savedReportName || template.name}</h1>
            <p className="sales-subtitle">
              {template.description || "Run, save, and export this report."}
            </p>
          </div>

          <ReportToolbar
            onBack={() => navigate("/reports")}
            onRun={handleRunReport}
            onExportCsv={handleExportCsv}
            onExportPdf={handleExportPdf}
            onSave={handleSaveReport}
            canRun={permissions.can_run}
            canExportCsv={permissions.can_export_csv}
            canExportPdf={permissions.can_export_pdf}
            running={running}
            exportingCsv={exportingCsv}
            exportingPdf={exportingPdf}
            savingReport={savingReport}
          />
        </div>

        {BuilderComponent ? (
          <BuilderComponent
            template={template}
            coverageTypes={coverageTypes}
            coverageTypesLoading={coverageTypesLoading}
            coverageTypesError={coverageTypesError}
            carriers={carriers}
            carriersLoading={carriersLoading}
            agents={agents}
            agentsLoading={agentsLoading}
            opportunityStatuses={opportunityStatuses}
            opportunityStatusesLoading={opportunityStatusesLoading}
            selectedOwnedCoverageTypes={selectedOwnedCoverageTypes}
            setSelectedOwnedCoverageTypes={setSelectedOwnedCoverageTypes}
            selectedMissingCoverageTypes={selectedMissingCoverageTypes}
            setSelectedMissingCoverageTypes={setSelectedMissingCoverageTypes}
            selectedCarrierId={selectedCarrierId}
            setSelectedCarrierId={setSelectedCarrierId}
            stateFilter={stateFilter}
            setStateFilter={setStateFilter}
            zipCodeFilter={zipCodeFilter}
            setZipCodeFilter={setZipCodeFilter}
            onClearFilters={handleClearFilters}
            filters={
              isSoldPolicies
                ? soldPoliciesFilters
                : isPolicyBook
                  ? policyBookFilters
                  : isUpcomingEffective
                    ? upcomingEffectiveFilters
                    : isOpportunityPipeline
                      ? opportunityPipelineFilters
                      : []
            }
            setFilters={
              isSoldPolicies
                ? setSoldPoliciesFilters
                : isPolicyBook
                  ? setPolicyBookFilters
                  : isUpcomingEffective
                    ? setUpcomingEffectiveFilters
                    : isOpportunityPipeline
                      ? setOpportunityPipelineFilters
                      : () => {}
            }
            datePreset={upcomingEffectivePreset}
            setDatePreset={setUpcomingEffectivePreset}
            reportResults={reportResults}
            builtConfig={builtConfig}
            formatFieldName={formatFieldName}
          />
        ) : (
          <div className="sales-section-card">
            <div className="sales-section-head">
              <h2>Coming Soon</h2>
              <p>This report builder has not been implemented yet.</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ReportBuilderPage;